import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

// Shared dialog ownership prevents older requests from replacing a newer selection.
let quickAddRequestId = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, { content: Element, expires: number }>} */
  #cachedContent = new Map();
  #pendingContent = new Map();
  #cacheGeneration = 0;
  #prefetchTimer;
  #onVariantSelected = (event) => this.#updateQuickAddButtonState(event);
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#cartUpdateAbortController = new AbortController();
    this.addEventListener('pointerenter', this.#schedulePrefetch);
    this.addEventListener('pointerleave', this.#cancelPrefetch);
    this.addEventListener('focusin', this.#prefetch);
    this.addEventListener('touchstart', this.#prefetch, { passive: true });
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cancelPrefetch();
    this.removeEventListener('pointerenter', this.#schedulePrefetch);
    this.removeEventListener('pointerleave', this.#cancelPrefetch);
    this.removeEventListener('focusin', this.#prefetch);
    this.removeEventListener('touchstart', this.#prefetch);
    this.#cacheGeneration++;
    this.#pendingContent.clear();
    this.#cachedContent.clear();
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cacheGeneration++;
    this.#pendingContent.clear();
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  #cancelPrefetch = () => clearTimeout(this.#prefetchTimer);

  #schedulePrefetch = () => {
    this.#cancelPrefetch();
    this.#prefetchTimer = setTimeout(this.#prefetch, 120);
  };

  #prefetch = () => {
    const connection = navigator.connection;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    const url = this.productPageUrl;
    if (url) void this.#loadContent(url).catch(() => {});
  };

  #loadContent(url) {
    const cached = this.#cachedContent.get(url);
    if (cached && cached.expires > Date.now()) return Promise.resolve(cached.content);
    this.#cachedContent.delete(url);
    if (this.#pendingContent.has(url)) return this.#pendingContent.get(url);

    const generation = this.#cacheGeneration;
    const pending = this.fetchProductPage(url).then((html) => {
      const grid = html?.querySelector('[data-product-grid-content]');
      if (!grid) throw new Error('Product content is unavailable');
      const content = grid.cloneNode(true);
      if (generation === this.#cacheGeneration) {
        if (this.#cachedContent.size >= 4) {
          this.#cachedContent.delete(this.#cachedContent.keys().next().value);
        }
        this.#cachedContent.set(url, { content, expires: Date.now() + 60000 });
      }
      return content;
    }).finally(() => {
      if (this.#pendingContent.get(url) === pending) this.#pendingContent.delete(url);
    });
    this.#pendingContent.set(url, pending);
    return pending;
  }

  handleClick = async (event) => {
    event.preventDefault();
    this.#cancelPrefetch();
    const currentUrl = this.productPageUrl;
    const dialogComponent = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!currentUrl || !modalContent || !(dialogComponent instanceof QuickAddDialog)) return;

    const requestId = ++quickAddRequestId;
    let closed = false;
    const onClose = () => { closed = true; };
    dialogComponent.addEventListener(DialogCloseEvent.eventName, onClose, { once: true });
    const isCurrent = () => !closed && requestId === quickAddRequestId && this.isConnected;

    // Remove the previous product before opening the shared dialog.
    const status = document.createElement('div');
    status.setAttribute('role', 'status');
    status.style.cssText = 'grid-column:1/-1;display:grid;place-content:center;min-height:180px;padding:24px;text-align:center;';
    status.textContent = 'Loading product…';
    modalContent.replaceChildren(status);
    modalContent.setAttribute('aria-busy', 'true');
    this.#openQuickAddModal();

    try {
      const productGrid = await this.#loadContent(currentUrl);
      if (!isCurrent()) return;
      await this.updateQuickAddModal(productGrid.cloneNode(true));
      if (!isCurrent()) return;
      this.#updateVariantPicker(productGrid);
      this.#resetScroll();
    } catch (error) {
      if (!isCurrent()) return;
      const message = document.createElement('p');
      message.textContent = 'Unable to load this product. Please try again or view the product page.';
      const link = document.createElement('a');
      link.href = currentUrl;
      link.textContent = 'View product';
      status.replaceChildren(message, link);
      modalContent.replaceChildren(status);
    } finally {
      dialogComponent.removeEventListener(DialogCloseEvent.eventName, onClose);
      if (requestId === quickAddRequestId) modalContent.removeAttribute('aria-busy');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(productPageUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(`Failed to fetch product page: HTTP ${response.status}`);
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } finally {
      clearTimeout(timeout);
      if (this.#abortController === controller) this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
